import { useEffect, useRef, useState } from 'react';
import { isAppPath, sectionForPath } from '../routes';

const EXIT_MS = 240;
const ENTER_MS = 420;
const currentPath = () => window.location.pathname.replace(/\/+$/, '') || '/';
const reduceMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

export default function PageTransition({ children }) {
    const [path, setPath] = useState(currentPath);
    const [phase, setPhase] = useState('idle');
    const timer = useRef(null);
    const pending = useRef(false);
    const focusNext = useRef(false);
    const root = useRef(null);

    useEffect(() => {
        const onBack = () => {
            clearTimeout(timer.current);
            pending.current = false;
            focusNext.current = true;
            setPath(currentPath());
            setPhase('idle');
        };
        window.addEventListener('popstate', onBack);
        return () => {
            clearTimeout(timer.current);
            window.removeEventListener('popstate', onBack);
        };
    }, []);

    useEffect(() => {
        const section = sectionForPath(path);
        if (section) document.title = section === 'home' ? 'Jonathan Edward' : `${section[0].toUpperCase()}${section.slice(1)} — Jonathan Edward`;
        if (!focusNext.current) return;
        focusNext.current = false;
        const heading = root.current?.querySelector('h1');
        heading?.setAttribute('tabindex', '-1');
        heading?.focus({ preventScroll: true });
    }, [path]);

    const navigateTo = href => {
        const url = new URL(href, window.location.href);
        const next = url.pathname.replace(/\/+$/, '') || '/';
        if (next === path || pending.current) return;
        // Portfolio panels already cross-fade. Keep their shared shell mounted.
        if (sectionForPath(path) && sectionForPath(next)) {
            clearTimeout(timer.current);
            window.history.pushState({}, '', url.pathname + url.search + url.hash);
            focusNext.current = true;
            setPath(next);
            setPhase('idle');
            return;
        }
        pending.current = true;
        clearTimeout(timer.current);
        const changePage = () => {
            window.history.pushState({}, '', url.pathname + url.search + url.hash);
            focusNext.current = true;
            setPath(next);
            pending.current = false;
            setPhase(reduceMotion() ? 'idle' : 'entering');
            timer.current = setTimeout(() => setPhase('idle'), ENTER_MS);
        };
        if (reduceMotion()) changePage();
        else {
            setPhase('leaving');
            timer.current = setTimeout(changePage, EXIT_MS);
        }
    };

    const navigate = event => {
        if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const link = event.target.closest('a[href]');
        if (!link || link.hasAttribute('download') || (link.target && link.target !== '_self')) return;
        const url = new URL(link.href, window.location.href);
        const next = url.pathname.replace(/\/+$/, '') || '/';
        if (url.origin !== window.location.origin || !isAppPath(next)) return;
        // Leave article anchors to the browser, but do not reload an active nav link.
        if (next === path && (url.hash || url.search !== window.location.search)) return;
        event.preventDefault();
        navigateTo(url.href);
    };

    return <div ref={root} className={`page-transition is-${phase}`} onClick={navigate}
        style={{ '--page-exit': `${EXIT_MS}ms`, '--page-enter': `${ENTER_MS}ms` }}>
        <div key={sectionForPath(path) ? 'portfolio' : path} inert={phase === 'leaving'}>{children(path, navigateTo)}</div>
    </div>;
}
